import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Download, FileDown, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  STATUS_LABELS, PARTNERSHIP_TYPES, PARTNERSHIP_TYPE_LABELS, CLOSURE_TYPE_LABELS,
  fmtCurrency, fmtDate,
} from '../lib/utils';

const PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

const STATUS_GROUPS = [
  { value: 'all', label: 'All' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'active', label: 'Active Pipeline' },
];

function startOfPeriod(p) {
  const now = new Date();
  if (p === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (p === 'quarter') {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), q, 1);
  }
  if (p === 'year') return new Date(now.getFullYear(), 0, 1);
  return null;
}

export default function ReportsPage() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [type, setType] = useState('all');
  const [statusG, setStatusG] = useState('all');
  const [period, setPeriod] = useState('all');
  const [member, setMember] = useState('all');

  useEffect(() => {
    api.get('/leads').then((r) => setLeads(r.data));
    api.get('/users').then((r) => setUsers(r.data));
  }, []);

  const filtered = leads.filter((l) => {
    if (type !== 'all' && l.partnership_type !== type) return false;
    if (member !== 'all' && l.assigned_to !== member) return false;
    if (statusG === 'closed_won' && l.status !== 'closed_won') return false;
    if (statusG === 'closed_lost' && l.status !== 'closed_lost') return false;
    if (statusG === 'active' && ['closed_won', 'closed_lost', 'inactive'].includes(l.status)) return false;
    if (period !== 'all') {
      const start = startOfPeriod(period);
      const ref = l.close_date || l.updated_at || l.created_at;
      if (ref && start && new Date(ref) < start) return false;
    }
    return true;
  });

  const userMap = Object.fromEntries(users.map((u) => [u.id, u.full_name]));

  const summary = {
    count: filtered.length,
    revenue: filtered.filter((l) => l.status === 'closed_won').reduce((s, l) => s + (l.revenue || 0), 0),
    pipeline: filtered.filter((l) => !['closed_won', 'closed_lost', 'inactive'].includes(l.status)).reduce((s, l) => s + (l.estimated_revenue || 0), 0),
    commission: filtered.filter((l) => l.status === 'closed_won').reduce((s, l) => s + (l.commission || 0), 0),
  };

  const csvEscape = (v) => {
    const s = String(v ?? '');
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCSV = () => {
    const headers = [
      'Company', 'Contact', 'Title', 'Email', 'Phone', 'Industry', 'Lead Source', 'Campaign',
      'Partnership Type', 'Partnership Tier', 'Status', 'Closure Type', 'Estimated Value',
      'Actual Closed Value', 'Revenue', 'Commission', 'Assigned To',
      'Close Date', 'Next Follow-Up',
    ];
    const rows = filtered.map((l) => [
      l.company_name, l.primary_contact, l.title, l.email, l.phone, l.industry,
      l.lead_source, l.campaign,
      PARTNERSHIP_TYPE_LABELS[l.partnership_type] || '', l.partnership_tier,
      STATUS_LABELS[l.status], CLOSURE_TYPE_LABELS[l.closure_type] || '',
      l.estimated_revenue, l.actual_closed_value, l.revenue, l.commission,
      userMap[l.assigned_to] || '',
      l.close_date ? fmtDate(l.close_date) : '', l.next_followup_date ? fmtDate(l.next_followup_date) : '',
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aawta-partnerships-${type}-${statusG}-${period}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    // Branded header
    doc.setFillColor(14, 25, 68); // navy
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 70, 'F');
    doc.setFillColor(233, 30, 140); // magenta circle
    doc.circle(36, 35, 14, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('A', 33, 39);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('AAWTA Partnership Hub', 60, 32);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Partnership Report', 60, 50);

    // Filter summary
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(9);
    const filterLine = [
      `Type: ${type === 'all' ? 'All' : PARTNERSHIP_TYPE_LABELS[type]}`,
      `Status: ${statusG}`,
      `Period: ${period}`,
      `Team: ${member === 'all' ? 'All' : (userMap[member] || '')}`,
      `Generated: ${new Date().toLocaleDateString()}`,
    ].join('   |   ');
    doc.text(filterLine, 40, 90);

    // Summary cards
    doc.setTextColor(14, 25, 68);
    doc.setFontSize(10);
    const cardY = 110;
    const cardW = 170;
    const cards = [
      { l: 'Records', v: String(summary.count) },
      { l: 'Revenue', v: fmtCurrency(summary.revenue) },
      { l: 'Pipeline Value', v: fmtCurrency(summary.pipeline) },
      { l: 'Commission Owed', v: fmtCurrency(summary.commission) },
    ];
    cards.forEach((c, i) => {
      const x = 40 + i * (cardW + 10);
      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, 50, 8, 8, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(c.l.toUpperCase(), x + 12, cardY + 18);
      doc.setFontSize(14);
      doc.setTextColor(14, 25, 68);
      doc.setFont('helvetica', 'bold');
      doc.text(c.v, x + 12, cardY + 38);
      doc.setFont('helvetica', 'normal');
    });

    // Table
    const head = [['Company', 'Type', 'Tier', 'Status', 'Closure', 'Est. Value', 'Revenue', 'Team Member', 'Close Date']];
    const body = filtered.map((l) => [
      l.company_name,
      PARTNERSHIP_TYPE_LABELS[l.partnership_type] || '—',
      l.partnership_tier || '—',
      STATUS_LABELS[l.status] || '—',
      CLOSURE_TYPE_LABELS[l.closure_type] || '—',
      fmtCurrency(l.estimated_revenue),
      fmtCurrency(l.revenue),
      userMap[l.assigned_to] || '—',
      l.close_date ? fmtDate(l.close_date) : '—',
    ]);

    autoTable(doc, {
      head,
      body,
      startY: 175,
      margin: { left: 40, right: 40 },
      styles: { fontSize: 8, cellPadding: 6, textColor: [51, 65, 85] },
      headStyles: { fillColor: [14, 25, 68], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' } },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageStr = `Page ${data.pageNumber} of ${pageCount}`;
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(pageStr, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 20);
        doc.text('AAWTA Partnership Hub · Confidential', 40, doc.internal.pageSize.getHeight() - 20);
      },
    });

    doc.save(`aawta-partnerships-${type}-${statusG}-${period}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-magenta" />
          <h3 className="font-heading font-bold text-navy">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Partnership Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="report-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusG} onValueChange={setStatusG}>
              <SelectTrigger data-testid="report-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_GROUPS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="report-period-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Team Member</Label>
            <Select value={member} onValueChange={setMember}>
              <SelectTrigger data-testid="report-member-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Records', value: summary.count, testid: 'report-count' },
          { label: 'Revenue', value: fmtCurrency(summary.revenue), testid: 'report-revenue' },
          { label: 'Pipeline Value', value: fmtCurrency(summary.pipeline), testid: 'report-pipeline' },
          { label: 'Commission Owed', value: fmtCurrency(summary.commission), testid: 'report-commission' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={s.testid}>
            <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">{s.label}</div>
            <div className="font-heading text-2xl font-bold text-navy mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={exportPDF} variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white" data-testid="export-pdf">
          <FileDown className="w-4 h-4 mr-2" /> Export PDF
        </Button>
        <Button onClick={exportCSV} className="bg-magenta hover:bg-magenta/90" data-testid="export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                {['Company', 'Type', 'Tier', 'Status', 'Closure', 'Est. Value', 'Revenue', 'Team Member', 'Close Date'].map((h) => (
                  <th key={h} className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-row-${l.id}`}>
                  <td className="px-4 py-3 font-semibold text-sm text-navy">{l.company_name}</td>
                  <td className="px-4 py-3 text-xs">{PARTNERSHIP_TYPE_LABELS[l.partnership_type] || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{l.partnership_tier || '—'}</td>
                  <td className="px-4 py-3 text-xs">{STATUS_LABELS[l.status]}</td>
                  <td className="px-4 py-3 text-xs">{CLOSURE_TYPE_LABELS[l.closure_type] || '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold">{fmtCurrency(l.estimated_revenue)}</td>
                  <td className="px-4 py-3 text-sm">{fmtCurrency(l.revenue)}</td>
                  <td className="px-4 py-3 text-xs">{userMap[l.assigned_to] || '—'}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(l.close_date)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-slate-500 text-sm">No records match</td></tr>
              )}
            </tbody>
          </table>
          {filtered.length > 50 && (
            <div className="px-4 py-3 text-xs text-slate-500 bg-slate-50 border-t border-slate-100">
              Showing 50 of {filtered.length} records — export CSV for the full list.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
